import { eventQBus } from "@otto-ec/global-resources/event-q-bus";
import { moduleLogger } from "../util";
import { ActivationClient } from "./ActivationClient";
import { ActivationStatus, type Detailview, type Tracking } from "./Detailview";
import { Slider } from "./Slider";
import { ActivationTrigger } from "./events/ActivationTrigger";
import type { AddToBasketEvent } from "./events/AddToBasketEvent";
import { registerSeenHandlerOnElement } from "./tracking/SeenHandler";
import type { TrackingService } from "./tracking/TrackingService";

const log = moduleLogger.scope("detailview.service");

/*                                                                                                    */
/*                                                                              */
const MAX_CLEANUP_PERIOD_IN_MILLIS = 20 * 24 * 60 * 60 * 1000;

export const EXPIRED_TEXT = "Diese Aktion ist leider abgelaufen";

export class DetailviewService {
	constructor(private readonly trackingService: TrackingService) {}

	private readonly activationClient = new ActivationClient();

	public initialize(variationId: string, detailviewHtml: HTMLElement) {
		const detailviewHtmlElement = detailviewHtml.firstElementChild;

		if (!(detailviewHtmlElement && detailviewHtmlElement instanceof HTMLElement && variationId)) {
			return;
		}

		const sublineElement = detailviewHtmlElement.querySelector(".benefit-detailview_partnerbenefit-subline");
		if (!sublineElement || !(sublineElement instanceof HTMLElement)) {
			log.error("Subline element not found");
			return;
		}

		const detailview = buildDetailview(detailviewHtmlElement, sublineElement, variationId);
		if (!detailview) {
			return;
		}

		this.handleAlreadyActivatedBenefit(detailview);
		expirationHandler(detailview, sublineElement);

		const activationSlider = detailviewHtmlElement.querySelector(".activation_slider-slider-container");
		const activatedSlider = detailviewHtmlElement.querySelector(".activation_slider-checkmark-container");
		if (activationSlider && activationSlider instanceof HTMLElement && activatedSlider && activatedSlider instanceof HTMLElement) {
			if (!this.initActivationSlider(detailview, variationId, activationSlider, activatedSlider, detailviewHtmlElement)) {
				return;
			}
		}

		log.info("Send load tracking event and set visible handler");
		this.trackingService.sendDetailviewBenefitLoadedEvent(detailview, null);
		registerSeenHandlerOnElement(detailviewHtmlElement, () => {
			this.trackingService.sendDetailviewBenefitVisibleEvent(detailview);
		});

		const toAppButtonElement = detailviewHtml.querySelector("#benefit-detailview_app_btn");
		if (toAppButtonElement && toAppButtonElement instanceof HTMLElement) {
			toAppButtonElement.addEventListener("click", () => {
				this.trackingService.sendDetailviewBenefitClickEvent(detailview);
			});
		}

		eventQBus.on("ft1.order-core.addToBasketResult", addToBasketHandler(this.trackingService, detailview).bind(this));
	}

	private initActivationSlider(detailview: Detailview, variationId: string, activationSlider: HTMLElement, activatedSlider: HTMLElement, detailviewHtmlElement: HTMLElement): boolean {
		const successCallback = () => {
			eventQBus.emit("ft9.benefit.active", {
				id: detailview.benefitId,
				active: true,
				variationId: variationId,
				trigger: ActivationTrigger.ACTIVATED_BY_USER,
			});
			activationSlider.remove();
			activatedSlider.style.display = "inherit";
		};

		const slider = new Slider(variationId);
		const errorCallback = () => {
			eventQBus.emit("assets.snackbar.trigger", {
				text: "Das hat leider nicht geklappt. Bitte probiere es noch einmal, danke.",
			});
			slider.reset(detailviewHtmlElement);
		};

		const activationUrl = activationSlider.dataset.activationUrl;

		if (!activationUrl) {
			log.error("Missing activationUrl");
			return false;
		}
		slider.init(sliderClickHandler(this.trackingService, detailview, activationUrl, this.activationClient, successCallback, errorCallback), detailviewHtmlElement);
		return true;
	}

	private handleAlreadyActivatedBenefit(detailview: Detailview) {
		if (detailview.activationStatus === ActivationStatus.ACTIVATED) {
			eventQBus.emit("ft9.benefit.active", {
				id: detailview.benefitId,
				active: true,
				variationId: detailview.variationId,
				trigger: ActivationTrigger.FROM_DB_STORAGE,
			});
		}
	}
}

function buildDetailview(detailviewBenefitElement: HTMLElement, sublineElement: HTMLElement, variationId: string): Detailview | null {
	const isAppOnly = sublineElement.dataset.isAppOnly === "true";
	const activationStatus = sublineElement.dataset.activationStatus as ActivationStatus;

	const benefitId = sublineElement.dataset.benefitId;
	if (!benefitId) {
		log.error("Missing benefitId");
		return null;
	}
	const activeUntil = sublineElement.dataset.activeUntil;
	if (!activeUntil) {
		log.error("Missing activeUntil");
		return null;
	}

	const tracking = detailviewBenefitElement.dataset.tracking;

	if (!tracking) {
		log.error("Missing tracking");
		return null;
	}

	return {
		benefitId: benefitId,
		activeUntil: activeUntil,
		activationStatus: activationStatus,
		tracking: JSON.parse(tracking) as Tracking,
		variationId: variationId,
		isAppOnly: isAppOnly,
	};
}

function expirationHandler(detailview: Detailview, targetElement: HTMLElement) {
	const end: Date = new Date(detailview.activeUntil);
	const delayInMillis: number = Math.min(Math.max(end.getTime() - new Date().getTime() - 1000, 1), MAX_CLEANUP_PERIOD_IN_MILLIS);

	log.info(`Expiration delay: ${delayInMillis} ms`);

	window.setTimeout(() => {
		targetElement.innerHTML = `<span class="benefit-detailview_partnerbenefit-scarcity">${EXPIRED_TEXT}</span>`;
		eventQBus.emit("ft9.benefit.active", {
			id: detailview.benefitId,
			active: false,
			variationId: detailview.variationId,
			trigger: ActivationTrigger.EXPIRED_BENEFIT,
		});
	}, delayInMillis);
}

function sliderClickHandler(trackingService: TrackingService, detailview: Detailview, activationUrl: string, activationClient: ActivationClient, successCallback: () => void, errorCallback: () => void) {
	return () => {
		log.info("Slider clicked - Console log");
		log.info(`Detailview: ${detailview}`);

		const errorLoggingPayload = {
			benefit_id: detailview.benefitId,
			benefit_active_until: detailview.activeUntil,
		};
		activationClient.activateViaUrl(activationUrl, errorLoggingPayload).then((success) => {
			if (success) {
				successCallback();
			} else {
				errorCallback();
			}
			trackingService.sendDetailviewBenefitSwipeEvent(detailview, success);
		});
	};
}

function addToBasketHandler(trackingService: TrackingService, detailview: Detailview) {
	return (event: AddToBasketEvent) => {
		if (!event.variationId || !event.eventMergeId) {
			return;
		}
		trackingService.sendDetailviewBenefitMergeEvent(detailview, event.eventMergeId);
	};
}
